import { EErrorLevel, logger } from '@web_opxp/features';
import orderBy from 'lodash/orderBy';
import { GLOBAL_ACHIEVEMENTS_LIST, GLOBAL_ACHIEVEMENTS_SUBTYPES_MAP } from './constants';
import { IAchievementsState, statePropName } from './slice';
import { EAchievementType, IAchievement, IAchievementsData, IGlobalAchievement, TGlobalAchievements } from './types';
import {
  countAccuracyPerfectAchievements,
  countGoalReachedAchievements,
  countHistogramPercentageAchievements,
  filterBySyncTimestamp,
  filterByWorkoutId,
  findMaxTierOfGlobalAchievement,
  sortByTierAndUniqueType,
} from './utils';

export interface IAppState {
  // this feature state
  [statePropName]: IAchievementsState;
  // other features we don't care of
  [otherKeys: string]: any;
}

const getFeatureState = (state: IAppState) => state[statePropName];

export const isLoading = (state: IAppState) => getFeatureState(state).loading;

export const getAllSingleAchievements = (state: IAppState) => getFeatureState(state).single;

export const getSingleAchivementsForWorkout = (state: IAppState, workoutId: number) =>
  filterByWorkoutId(getAllSingleAchievements(state), workoutId);

export const getGlobalAchievements = (state: IAppState) =>
  getSingleAchivementsForWorkout(state, 0) as Array<IGlobalAchievement>;

export const getSingleAchievementsForSiblings = (state: IAppState, siblings: number[]) => {
  const achievements: IAchievement[] = [];
  siblings.forEach((workoutId) => {
    const workoutAchievements = getSingleAchivementsForWorkout(state, workoutId);
    achievements.push(...workoutAchievements);
  });
  const global: IGlobalAchievement[] = getGlobalAchievements(state);
  achievements.push(...global);
  return achievements;
};

export const getAllMultiAchievements = (state: IAppState) => getFeatureState(state).multi;

export const getMultiAchivementsForWorkout = (state: IAppState, workoutId: number) =>
  filterByWorkoutId(getAllMultiAchievements(state), workoutId);

export const getMultiAchievementsForSiblings = (state: IAppState, siblings: number[]) => {
  const achievements: IAchievement[] = [];
  siblings.forEach((workoutId) => {
    const workoutAchievements = getMultiAchivementsForWorkout(state, workoutId);
    achievements.push(...workoutAchievements);
  });
  const global: IGlobalAchievement[] = getGlobalAchievements(state);
  achievements.push(...global);
  return achievements;
};

export const getAllAchievements = (state: IAppState) => {
  const achievements: IAchievementsData = {
    single: getAllSingleAchievements(state),
    multi: getAllMultiAchievements(state),
  };
  return achievements;
};

export const getAchievementsToSync = (state: IAppState) => {
  const achievements: IAchievementsData = {
    single: filterBySyncTimestamp(getAllSingleAchievements(state)),
    multi: filterBySyncTimestamp(getAllMultiAchievements(state)),
  };
  return achievements;
};

export const getCurrentAchievements = (state: IAppState) => getFeatureState(state).current;

export const hasNewAchievements = (state: IAppState) => getCurrentAchievements(state).length > 0;

export const getCurrentExerciseId = (state: IAppState) => getFeatureState(state).exerciseId;

export const getCurrentScore = (state: IAppState) => getFeatureState(state).score;

const getGlobalStats = (state: IAppState) => getFeatureState(state).stats;

export const getWorkoutsNumber = (state: IAppState) => getGlobalStats(state).numberWorkouts;

export const getPlayTimeLength = (state: IAppState) => Number((getWorkoutsNumber(state) * 90) / 3600);

export const getFinishedSessions = (state: IAppState) => getGlobalStats(state).finishedSessions;

export const getGoalReachedTimes = (state: IAppState) => {
  const achievements = getAllMultiAchievements(state);
  return countGoalReachedAchievements(achievements);
};

export const getAccuracyPerfectTimes = (state: IAppState) => {
  const achievements = getAllMultiAchievements(state);
  return countAccuracyPerfectAchievements(achievements);
};

export const getHistogramPercentageTimes = (state: IAppState, subType: number) => {
  const achievements = getAllSingleAchievements(state);
  return countHistogramPercentageAchievements(achievements, subType);
};

export const getNextGlobalAchievementTier = (state: IAppState, type: TGlobalAchievements) => {
  const achievements = getGlobalAchievements(state);
  const typeTiers = [...GLOBAL_ACHIEVEMENTS_SUBTYPES_MAP[type]].sort((a, b) => a - b);
  const maxAchievedTierAchievement = findMaxTierOfGlobalAchievement(achievements, type);
  if (!maxAchievedTierAchievement) {
    return typeTiers[0];
  }
  const tierIndex = typeTiers.findIndex((subType) => subType === maxAchievedTierAchievement.subType);
  if (tierIndex < 0) {
    logger(
      `global achievement ${EAchievementType[type]} tier ${maxAchievedTierAchievement.subType} not found in map`,
      EErrorLevel.Warning
    );
    return typeTiers[0];
  }
  // return next index or max index if last was found
  return typeTiers[Math.min(tierIndex + 1, typeTiers.length - 1)];
};

export const getCurrentCounterForGlobalAchievement = (state: IAppState, type: TGlobalAchievements) => {
  switch (type) {
    case EAchievementType.WorkoutNumber:
      return getWorkoutsNumber(state);
    case EAchievementType.PlayTimeLength:
      return getPlayTimeLength(state);
    case EAchievementType.FinishedSessions:
      return getFinishedSessions(state);
    case EAchievementType.GoalReachedTimes:
      return getGoalReachedTimes(state);
    case EAchievementType.AccuracyPerfectAgg:
      return getAccuracyPerfectTimes(state);
    case EAchievementType.HistogramPercentage90p:
      return getHistogramPercentageTimes(state, 90);
    case EAchievementType.HistogramPercentage50p:
      return getHistogramPercentageTimes(state, 50);
  }
};

export const getMaxReachedTierGlobalAchievementsSortedByTier = (state: IAppState) => {
  const allGlobalAchievements = getGlobalAchievements(state);
  return sortByTierAndUniqueType(allGlobalAchievements);
};

const getGlobalAchievementsProgressRatio = (state: IAppState) => {
  const progress: Array<{ type: TGlobalAchievements; subType: number; ratio: number }> = GLOBAL_ACHIEVEMENTS_LIST.map(
    (type, index) => {
      const currentCounter = getCurrentCounterForGlobalAchievement(state, type);
      const subType = getNextGlobalAchievementTier(state, type);
      const ratio = Math.floor((currentCounter / subType) * 100);
      return { type, subType, ratio, index };
    }
  );
  return orderBy(progress, ['ratio', 'index'], ['desc', 'asc']);
};

export const getGlobalAchievementsInProgress = (state: IAppState) => {
  const sortedByRatio = getGlobalAchievementsProgressRatio(state);
  const result: IGlobalAchievement[] = sortedByRatio.flatMap((value) => {
    if (!value.ratio || value.ratio >= 100) {
      return [];
    }
    return {
      type: value.type,
      subType: value.subType,
      syncTimestamp: 0,
      timestamp: 0,
      workoutId: 0,
    };
  });
  return result;
};

export const getGlobalAchievementsLocked = (state: IAppState) => {
  const sortedByRatio = getGlobalAchievementsProgressRatio(state);
  const result: IGlobalAchievement[] = sortedByRatio.flatMap((value) => {
    if (value.ratio > 0) {
      return [];
    }
    return {
      type: value.type,
      subType: value.subType,
      syncTimestamp: 0,
      timestamp: 0,
      workoutId: 0,
    };
  });
  return result;
};
